import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useI18n, SUPPORTED_LANGS } from "@/i18n/I18nContext";

export default function Header() {
  const { t, lang, switchLang, localePath } = useI18n();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const NAV = [
    { key: "home", to: { pathname: localePath("/"), hash: "" }, label: t("nav.home"), end: true },
    { key: "benefits", to: { pathname: localePath("/"), hash: "#programs" }, label: t("nav.benefits") },
    { key: "literacy", to: { pathname: localePath("/"), hash: "#literacy" }, label: t("nav.literacy") },
    { key: "consultations", to: { pathname: localePath("/"), hash: "#consultations" }, label: t("nav.consultations") },
    { key: "trust", to: { pathname: localePath("/"), hash: "#trust" }, label: t("nav.trust") },
  ];

  const langLabels = { mx: "ES · MX", en: "EN" };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 w-full bg-[#0F2942] text-white border-b border-[#173A5E]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <Link
            to={localePath("/")}
            data-testid="header-brand-link"
            className="flex items-center gap-3"
            onClick={() => setOpen(false)}
          >
            <span
              aria-hidden
              className="inline-flex items-center justify-center h-9 w-9 rounded-md bg-white text-[#0F2942] font-bold"
            >
              M
            </span>
            <span className="flex flex-col leading-tight">
              <span className="font-semibold tracking-tight text-base sm:text-lg">
                {t("nav.brand")}
              </span>
              <span className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
                {t("nav.brand_sub")}
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                data-testid={`header-nav-${item.key}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-md hover:bg-[#173A5E] ${
                    isActive && item.end ? "text-white" : "text-slate-200"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <div
              className="inline-flex items-center rounded-md border border-[#2A6B7C] bg-[#0F2942]"
              role="group"
              aria-label={t("nav.language")}
            >
              <Globe size={14} className="ml-2 text-[#94A3B8]" aria-hidden />
              {SUPPORTED_LANGS.map((code) => (
                <button
                  type="button"
                  key={code}
                  onClick={() => switchLang(code)}
                  data-testid={`header-lang-${code}`}
                  className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-widest ${
                    lang === code
                      ? "text-white"
                      : "text-slate-300 hover:text-white"
                  }`}
                  aria-pressed={lang === code}
                >
                  {langLabels[code]}
                </button>
              ))}
            </div>
            <Link
              to={{ pathname: localePath("/"), hash: "#lead-form" }}
              data-testid="header-cta-primary"
              className="inline-flex items-center rounded-md bg-[#1B4D3E] hover:bg-[#123528] text-white px-4 py-2 text-sm font-semibold"
            >
              {t("nav.cta")}
            </Link>
          </div>

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-md hover:bg-[#173A5E]"
            data-testid="header-mobile-toggle"
            aria-expanded={open}
            aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          className="lg:hidden border-t border-[#173A5E] bg-[#0F2942]"
          data-testid="header-mobile-menu"
        >
          <div className="px-4 py-4 space-y-1">
            {NAV.map((item) => (
              <Link
                key={item.key}
                to={item.to}
                data-testid={`header-mobile-nav-${item.key}`}
                className="block px-3 py-2 rounded-md text-sm font-medium text-slate-100 hover:bg-[#173A5E]"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-[#173A5E] mt-3">
              <div className="text-[11px] uppercase tracking-widest text-[#94A3B8] px-3 pb-2">
                {t("nav.language")}
              </div>
              <div className="flex items-center gap-2 px-3">
                {SUPPORTED_LANGS.map((code) => (
                  <button
                    key={code}
                    type="button"
                    onClick={() => {
                      switchLang(code);
                      setOpen(false);
                    }}
                    data-testid={`header-mobile-lang-${code}`}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-widest ${
                      lang === code
                        ? "bg-white text-[#0F2942]"
                        : "border border-[#2A6B7C] text-slate-100"
                    }`}
                  >
                    {langLabels[code]}
                  </button>
                ))}
              </div>
              <div className="px-3 pt-4">
                <Link
                  to={{ pathname: localePath("/"), hash: "#lead-form" }}
                  data-testid="header-mobile-cta"
                  className="block text-center rounded-md bg-[#1B4D3E] text-white px-4 py-2 text-sm font-semibold"
                  onClick={() => setOpen(false)}
                >
                  {t("nav.cta")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
